/*
 This file is part of GNU Taler
 (C) 2022-2024, 2026 Taler Systems S.A.

 GNU Taler is free software; you can redistribute it and/or modify it under the
 terms of the GNU General Public License as published by the Free Software
 Foundation; either version 3, or (at your option) any later version.

 GNU Taler is distributed in the hope that it will be useful, but WITHOUT ANY
 WARRANTY; without even the implied warranty of MERCHANTABILITY or FITNESS FOR
 A PARTICULAR PURPOSE.  See the GNU General Public License for more details.

 You should have received a copy of the GNU General Public License along with
 GNU Taler; see the file COPYING.  If not, see <http:
 */
import {
  HttpStatusCode,
  TalerCorebankApi,
  TalerErrorCode,
  assertUnreachable,
} from "@gnu-taler/taler-util";
import {
  Attention,
  AsyncButton,
  RouteDefinition,
  useBankCoreApiContext,
  useNotificationContext,
  useNotifiedOperation,
  useTranslationContext,
} from "@gnu-taler/web-util/browser";
import { Fragment, VNode, h } from "preact";
import { useState } from "preact/hooks";
import { useSessionState } from "../../hooks/session.js";
import { OperationError } from "../../components/OperationError.js";
import { AccountForm } from "./AccountForm.js";

export function CreateNewAccount({
  routeCancel,
  onCreateSuccess,
}: {
  routeCancel: RouteDefinition;
  onCreateSuccess: () => void;
}): VNode {
  const { i18n } = useTranslationContext();
  const { state: credentials } = useSessionState();
  const token =
    credentials.status !== "loggedIn" ? undefined : credentials.token;
  const {
    lib: { bank: api },
  } = useBankCoreApiContext();

  const [submitAccount, setSubmitAccount] = useState<
    TalerCorebankApi.RegisterAccountRequest | undefined
  >();

  const { showError, showSuccess } = useNotificationContext();

  // i18n.str`create account`,
  const create = useNotifiedOperation<
    Awaited<ReturnType<typeof api.createAccount>>,
    Parameters<typeof api.createAccount>
  >((ct, t, ac) => api.createAccount(t, ac), {
    onSuccess: showSuccess((success, token, account) => {
      onCreateSuccess();
      // Translators: The placeholder is a newly generated account password.
      return i18n.str`Account created with password "${account.password}".`;
    }),
    onFail: showError(i18n.str`Failed to create a new account.`, (fail) => {
      switch (fail.case) {
        case HttpStatusCode.BadRequest:
          return i18n.str`Server replied that phone or email is invalid`;
        case HttpStatusCode.Unauthorized:
          return i18n.str`The rights to perform the operation are not sufficient`;
        case TalerErrorCode.BANK_REGISTER_USERNAME_REUSE:
          return i18n.str`Account username is already taken`;
        case TalerErrorCode.BANK_REGISTER_PAYTO_URI_REUSE:
          return i18n.str`Account ID is already taken`;
        case TalerErrorCode.BANK_UNALLOWED_DEBIT:
          return i18n.str`Bank ran out of bonus credit.`;
        case TalerErrorCode.BANK_RESERVED_USERNAME_CONFLICT:
          return i18n.str`This account username is reserved and cannot be used.`;
        case TalerErrorCode.BANK_NON_ADMIN_PATCH_DEBT_LIMIT:
          return i18n.str`Only an administrator is allowed to set the debt limit.`;
        case TalerErrorCode.BANK_MISSING_TAN_INFO:
          return i18n.str`No information for the selected authentication channel.`;
        case TalerErrorCode.BANK_TAN_CHANNEL_NOT_SUPPORTED:
          return i18n.str`Authentication channel is not supported.`;
        case TalerErrorCode.BANK_NON_ADMIN_SET_TAN_CHANNEL:
          return i18n.str`Only admin can create accounts with second factor authentication.`;
        case TalerErrorCode.BANK_NON_ADMIN_SET_CONVERSION_RATE_CLASS:
          return i18n.str`Only the administrator can change the conversion rate.`;
        case TalerErrorCode.BANK_CONVERSION_RATE_CLASS_UNKNOWN:
          return i18n.str`The conversion rate class doesn't exist.`;
        case TalerErrorCode.BANK_PASSWORD_TOO_SHORT:
          return i18n.str`The password is too short. Can't have less than 8 characters.`;
        case TalerErrorCode.BANK_PASSWORD_TOO_LONG:
          return i18n.str`The password is too long. Can't have more than 64 characters.`;
        default:
          assertUnreachable(fail);
      }
    }),
  });

  if (!(credentials.status === "loggedIn" && credentials.isUserAdministrator)) {
    return (
      <Fragment>
        <Attention type="warning" title={i18n.str`Can't create accounts`}>
          <i18n.Translate>
            Only a system administrator can create accounts.
          </i18n.Translate>
        </Attention>
        <div class="mt-5 sm:mt-6">
          <a
            href={routeCancel.url({})}
            data-name="close"
            class="inline-flex w-full justify-center rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
          >
            <i18n.Translate>Close</i18n.Translate>
          </a>
        </div>
      </Fragment>
    );
  }

  return (
    <section class="mt-2">
      <a
        href={routeCancel.url({})}
        class="inline-flex items-center text-sm font-semibold text-brand hover:underline"
      >
        <span class="mr-1" aria-hidden="true">
          ←
        </span>
        <i18n.Translate>Back to accounts</i18n.Translate>
      </a>
      <div class="mx-auto mt-6 max-w-4xl">
        <h1 class="text-2xl font-semibold text-brand">
          <i18n.Translate>New bank account</i18n.Translate>
        </h1>
        <p class="mt-2 text-sm text-gray-600">
          <i18n.Translate>
            Create an account and configure its initial access and profile.
          </i18n.Translate>
        </p>
        <div class="mt-6">
          <AccountForm
            template={undefined}
            purpose="create"
            onChange={(a) => {
              setSubmitAccount(a);
            }}
          >
            <div class="border-t border-onBackground/10 bg-background/60 px-5 py-4 sm:px-8">
              <OperationError class="mb-4" />
              <div class="flex items-center justify-between gap-x-6">
                <a
                  href={routeCancel.url({})}
                  data-name="cancel"
                  class="text-sm font-semibold leading-6 text-onBackground hover:underline"
                >
                  <i18n.Translate>Cancel</i18n.Translate>
                </a>
                <AsyncButton
                  submit
                  name="create"
                  class="disabled:opacity-50 disabled:cursor-default cursor-pointer rounded-md bg-primary px-3 py-2 text-sm font-semibold text-onPrimary shadow-sm hover:bg-primaryHover hover:text-white focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  disabled={!submitAccount || !token}
                  onClick={() =>
                    create.run(
                      { type: "bearer", token: token! },
                      submitAccount!,
                    )
                  }
                >
                  <i18n.Translate>Create account</i18n.Translate>
                </AsyncButton>
              </div>
            </div>
          </AccountForm>
        </div>
      </div>
    </section>
  );
}
